"use client";

import { useActionState, useEffect, useRef, useState } from "react";
import { takeContactPrefill } from "@/lib/chat/contact-prefill";
import { useTranslations } from "next-intl";
import { CheckCircle } from "@phosphor-icons/react/dist/ssr";
import Select from "@/components/ui/Select";
import CopyButton from "@/components/ui/CopyButton";
import { isValidEmail, isValidPhone, filterPhoneInput } from "@/lib/validations";
import { submitContactAction } from "@/lib/actions/contact";
import type { ContactState } from "@/lib/actions/contact";
import {
  CONTACT_PREFILL_SERVICE_EVENT,
  type ContactPrefillServiceDetail,
} from "@/lib/events";
import { BusyLabel } from "@/components/ui/Spinner";

interface ContactFormProps {
  professionalId: string;
  proName: string;
  /** `name` is the professional's own (the value they receive); `label` is what
   *  the visitor reads, translated on a translated profile. */
  packages: { name: string; label?: string }[];
  prefillName?: string | null;
  prefillEmail?: string | null;
  /** Public slug — used to accept a chat hand-off (sessionStorage, slug-matched). */
  proSlug?: string;
}

export default function ContactForm({ professionalId, proName, packages, prefillName, prefillEmail, proSlug }: ContactFormProps) {
  const t = useTranslations("profile");
  const [state, action, isPending] = useActionState<ContactState, FormData>(
    submitContactAction,
    undefined,
  );
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());
  const [serviceValue, setServiceValue] = useState("");
  const phoneRef = useRef<HTMLInputElement>(null);
  // Chat assistant hand-off: the EN summary the user confirmed arrives via
  // sessionStorage (never a URL param — it is their need in prose). Read in an
  // effect (never in render — SSR has no sessionStorage), slug-matched, 10-min
  // TTL, cleared on read. Only seeds an EMPTY textarea.
  const [prefillMessage, setPrefillMessage] = useState("");
  useEffect(() => {
    if (!proSlug) return;
    const m = takeContactPrefill(proSlug);
    if (m) setPrefillMessage(m);
  }, [proSlug]);

  // Listen for package-click prefill events from PackageCard. The valid
  // service options are the package names plus "other"; an event payload that
  // doesn't match a package is ignored to avoid stuffing arbitrary text into
  // the dropdown's value.
  useEffect(() => {
    const validServices = new Set(packages.map((p) => p.name));
    function onPrefill(e: Event) {
      const detail = (e as CustomEvent<ContactPrefillServiceDetail>).detail;
      if (detail?.service && validServices.has(detail.service)) {
        setServiceValue(detail.service);
        setFieldErrors((prev) => { const n = new Set(prev); n.delete("service"); return n; });
        // Drop focus into the phone field — the highest-friction missing
        // piece for most users (the form pre-fills name + email from their
        // auth profile, so phone is typically the only manual input before
        // they get to the message). `preventScroll: true` keeps the
        // smooth-scroll PackageCard kicked off intact — focusing an input
        // otherwise triggers the browser's scroll-into-view, which
        // jump-cuts past the smooth animation in progress.
        phoneRef.current?.focus({ preventScroll: true });
      }
    }
    window.addEventListener(CONTACT_PREFILL_SERVICE_EVENT, onPrefill);
    return () => window.removeEventListener(CONTACT_PREFILL_SERVICE_EVENT, onPrefill);
  }, [packages]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    const form    = e.currentTarget;
    const name    = (form.elements.namedItem("name")    as HTMLInputElement).value.trim();
    const email   = (form.elements.namedItem("email")   as HTMLInputElement).value.trim();
    const phone   = (form.elements.namedItem("phone")   as HTMLInputElement).value.trim();
    const service = (form.elements.namedItem("service") as HTMLInputElement).value;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value.trim();
    const fields: string[] = [];
    if (!name)               fields.push("name");
    if (!email && !phone)    fields.push("email", "phone");
    if (email && !isValidEmail(email)) fields.push("email");
    if (phone && !isValidPhone(phone)) fields.push("phone");
    if (!service)            fields.push("service");
    if (!message)            fields.push("message");
    if (fields.length) {
      e.preventDefault();
      setFieldErrors(new Set(fields));
    } else {
      setFieldErrors(new Set());
    }
  }

  const normalBase = "border-b border-white/20";
  const errBase    = "border-b border-error-line";

  if (state?.status === "success") {
    return (
      <div className="mt-21 flex flex-col items-center py-21 text-center">
        {/* GREEN, not brand gold (owner, 2026-08-26) — see RequestForm. This
            panel is navy (#040044), so the BRIGHTER `#10B981` is the right
            token here: 7.5:1 on navy, where the darker #059669 used on white
            would sink into the background. */}
        <CheckCircle
          size={36}
          weight="fill"
          className="text-green"
          aria-hidden="true"
        />
        <p className="mt-13 font-serif text-lg text-white">
          {t("contactMessageSent")}
        </p>
        <p className="mt-8 text-sm leading-relaxed text-white/70">
          {t("contactInTouch", { name: proName.split(" ")[0] })}
        </p>
        {state.referralRef && (
          <div className="mt-21 w-full border-t border-white/15 pt-13">
            <p className="text-xs uppercase tracking-wider text-white/70">{t("contactReferralId")}</p>
            <p className="mt-5 inline-flex items-center gap-5 font-mono text-base font-semibold text-white">
              {state.referralRef}
              <CopyButton value={state.referralRef} ariaLabel={t("copyReferralId")} />
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <form
      action={action}
      onSubmit={handleSubmit}
      className="mt-21 space-y-0"
      noValidate
      aria-label={t("contactFormAria", { name: proName })}
    >
      {/* Hidden professional identifier */}
      <input type="hidden" name="professional_id" value={professionalId} />

      {state?.status === "error" && (
        <p
          role="alert"
          className="mb-13 rounded-control bg-red-900/40 px-13 py-8 text-xs text-red-200"
        >
          {state.message}
        </p>
      )}

      <div>
        <label htmlFor="contact-name" className="sr-only">
          {t("contactNameLabel")} <span aria-hidden="true">*</span>
        </label>
        <input
          id="contact-name"
          type="text"
          name="name"
          required
          aria-required="true"
          aria-invalid={fieldErrors.has("name")}
          aria-describedby={fieldErrors.has("name") ? "contact-name-error" : undefined}
          autoComplete="name"
          defaultValue={prefillName ?? undefined}
          placeholder={t("contactNamePlaceholder")}
          className={`input-on-navy w-full border-0 ${fieldErrors.has("name") ? `${errBase} focus-visible:border-error-line` : `${normalBase} focus-visible:border-gold`} bg-transparent py-13 text-sm text-white placeholder:text-white/70 focus-visible:outline-none`}
          onChange={() => setFieldErrors(prev => { const n = new Set(prev); n.delete("name"); return n; })}
        />
        {fieldErrors.has("name") && (
          <p id="contact-name-error" className="mt-3 text-[10px] text-[#FCA5A5]">{t("contactNameError")}</p>
        )}
      </div>

      <div>
        <label htmlFor="contact-email" className="sr-only">
          {t("contactEmailLabel")}
        </label>
        <input
          id="contact-email"
          type="email"
          name="email"
          aria-invalid={fieldErrors.has("email")}
          aria-describedby={fieldErrors.has("email") ? "contact-email-error" : undefined}
          autoComplete="email"
          defaultValue={prefillEmail ?? undefined}
          placeholder={t("contactEmailPlaceholder")}
          className={`input-on-navy w-full border-0 ${fieldErrors.has("email") ? `${errBase} focus-visible:border-error-line` : `${normalBase} focus-visible:border-gold`} bg-transparent py-13 text-sm text-white placeholder:text-white/70 focus-visible:outline-none`}
          onChange={() => setFieldErrors(prev => { const n = new Set(prev); n.delete("email"); n.delete("phone"); return n; })}
          onBlur={(e) => { const v = e.target.value.trim(); if (v && !isValidEmail(v)) setFieldErrors(prev => new Set([...prev, "email"])); }}
        />
        {fieldErrors.has("email") && (
          <p id="contact-email-error" className="mt-3 text-[10px] text-[#FCA5A5]">{t("contactEmailError")}</p>
        )}
      </div>

      <div>
        <label htmlFor="contact-phone" className="sr-only">
          {t("contactPhoneLabel")}
        </label>
        <input
          ref={phoneRef}
          id="contact-phone"
          type="tel"
          name="phone"
          autoComplete="tel"
          aria-invalid={fieldErrors.has("phone")}
          aria-describedby={fieldErrors.has("phone") ? "contact-phone-error" : undefined}
          placeholder={t("contactPhonePlaceholder")}
          className={`input-on-navy w-full border-0 ${fieldErrors.has("phone") ? `${errBase} focus-visible:border-error-line` : `${normalBase} focus-visible:border-gold`} bg-transparent py-13 text-sm text-white placeholder:text-white/70 focus-visible:outline-none`}
          onChange={(e) => { e.target.value = filterPhoneInput(e.target.value); setFieldErrors(prev => { const n = new Set(prev); n.delete("email"); n.delete("phone"); return n; }); }}
        />
        {fieldErrors.has("phone") && (
          <p id="contact-phone-error" className="mt-3 text-[10px] text-[#FCA5A5]">{t("contactPhoneError")}</p>
        )}
      </div>

      <p className="px-0 pt-5 text-[10px] text-white/70">
        {t("contactEmailOrPhone")}
      </p>

      <div>
        <label htmlFor="contact-service" className="sr-only">
          {t("contactServiceLabel")}
        </label>
        <Select
          id="contact-service"
          name="service"
          placeholder={t("contactServicePlaceholder")}
          required
          error={fieldErrors.has("service")}
          variant="dark"
          value={serviceValue}
          onChange={(v) => {
            setServiceValue(v);
            setFieldErrors(prev => { const n = new Set(prev); n.delete("service"); return n; });
          }}
          options={[
            ...packages.map((pkg) => ({ value: pkg.name, label: pkg.label ?? pkg.name })),
            { value: "other", label: t("contactOther") },
          ]}
        />
      </div>

      <div className="mt-13">
        <label htmlFor="contact-message" className="sr-only">
          {t("contactMessageLabel")} <span aria-hidden="true">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          key={prefillMessage ? "prefilled" : "empty"}
          defaultValue={prefillMessage || undefined}
          rows={4}
          required
          aria-required="true"
          placeholder={t("contactMessagePlaceholder")}
          className={`input-on-navy w-full resize-none border-0 ${fieldErrors.has("message") ? `${errBase} focus-visible:border-error-line` : `${normalBase} focus-visible:border-gold`} bg-transparent py-8 text-sm text-white placeholder:text-white/70 focus-visible:outline-none`}
          onChange={() => setFieldErrors(prev => { const n = new Set(prev); n.delete("message"); return n; })}
        />
      </div>

      <button
        type="submit"
        disabled={isPending}
        aria-disabled={isPending}
        className="mt-21 w-full rounded-control bg-gold py-13 text-sm font-semibold uppercase tracking-wider text-navy transition-colors hover:bg-gold-hover disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
      >
        <BusyLabel busy={isPending}>{isPending ? t("contactSending") : t("contactSend")}</BusyLabel>
      </button>
    </form>
  );
}
